// Components/HomePage/ProblemSection.jsx
import React from "react";
import { FiUser, FiTool, FiClock, FiFileText } from "react-icons/fi";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

export default function ProblemSection() {
  const problemsLeft = [
    {
      icon: <FiUser />,
      title: "Contractors Disappear Mid-Project",
      desc: "Many projects are left unfinished as contractors go silent, causing stress and delays.",
    },
    {
      icon: <FiTool />,
      title: "Poor Workmanship & Cut Corners",
      desc: "Subpar materials and sloppy execution leave homeowners with costly problems.",
    },
  ];

  const problemsRight = [
    {
      icon: <FiClock />,
      title: "Budget & Schedule Overruns",
      desc: "Projects often exceed the budget and timeline, creating frustration and uncertainty.",
    },
    {
      icon: <FiFileText />,
      title: "Confusing Permits & Plans",
      desc: "Navigating permits, plans, and city requirements alone can feel impossible.",
    },
  ];

  return (
    <section className="py-6 md:py-10 px-4 md:px-8 bg-white">
      <div className="max-w-7xl mx-auto">

        {/* Section Heading */}
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0 }}
          className="text-2xl md:text-3xl font-bold mb-6 text-center text-gray-900"
        >
          The Problem with Construction Projects
        </motion.h2>

        {/* Split Layout */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-6">

          {/* Right Problems — slides in from right */}
          <motion.div
            className="flex flex-col gap-5 lg:w-auto lg:flex-1 lg:max-w-sm"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0 }}
          >
            {problemsRight.map((problem, idx) => (
              <motion.div
                key={idx}
                variants={fadeRight}
                className="p-3 bg-white rounded-lg shadow-sm border-2 border-gray-700
                           transition-all duration-500 transform hover:scale-105
                           hover:shadow-[0_0_25px_rgba(0,0,0,0.2)]"
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <span className="text-white w-4 h-4">{problem.icon}</span>
                  </div>
                  <h3 className="text-base font-bold text-gray-900">{problem.title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{problem.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Center Image — fades in */}
          <motion.div
            className="lg:flex-shrink-0 flex justify-center"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0 }}
          >
            <img
              src="/Images/worker-construction.png"
              alt="Construction"
              className="w-32 md:w-44 lg:w-52 rounded-lg shadow-xl object-cover"
            />
          </motion.div>

          {/* Left Problems — slides in from left */}
          <motion.div
            className="flex flex-col gap-5 lg:w-auto lg:flex-1 lg:max-w-sm"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0 }}
          >
            {problemsLeft.map((problem, idx) => (
              <motion.div
                key={idx}
                variants={fadeLeft}
                className="p-3 bg-white rounded-lg shadow-sm border-2 border-gray-700
                           transition-all duration-500 transform hover:scale-105
                           hover:shadow-[0_0_25px_rgba(0,0,0,0.2)]"
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <span className="text-white w-4 h-4">{problem.icon}</span>
                  </div>
                  <h3 className="text-base font-bold text-gray-900">{problem.title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{problem.desc}</p>
              </motion.div>
            ))}
          </motion.div>

        </div>
      </div>
    </section>
  );
}